import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Copy, Loader2, X, Trash2, GitMerge, Clock, CheckCheck, ShieldAlert, RefreshCw, Scale } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_META = {
    certain: { label: 'Сигурен дубликат', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-300' },
    probable: { label: 'Вероятен дубликат', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-300' },
    possible: { label: 'Възможно сходен', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300' },
};

const COMPARE_FIELDS = [
    { key: 'name', label: 'Заглавие' },
    { key: 'brand', label: 'Марка' },
    { key: 'weight', label: 'Грамаж', render: (p) => p.weight ? `${p.weight} ${p.weight_unit || ''}` : '—' },
    { key: 'price', label: 'Цена', render: (p) => p.price != null ? `${p.price} €` : '—' },
    { key: 'stock', label: 'Наличност' },
    { key: 'sku', label: 'SKU' },
    { key: 'barcode', label: 'Баркод' },
    { key: 'category_name', label: 'Категория' },
    { key: 'subcategory', label: 'Подкатегория' },
    { key: 'is_draft', label: 'Статус', render: (p) => p.is_draft ? 'Чернова' : (p.is_visible === false ? 'Скрит' : 'Публикуван') },
    { key: 'created_at', label: 'Създаден', render: (p) => p.created_at ? new Date(p.created_at).toLocaleString('bg-BG') : '—' },
];

const MERGE_FIELDS = [
    { key: 'name', label: 'Заглавие' },
    { key: 'description', label: 'Описание' },
    { key: 'short_description', label: 'Кратко описание' },
    { key: 'image', label: 'Снимка' },
    { key: 'price', label: 'Цена' },
    { key: 'promotional_price', label: 'Промо цена' },
    { key: 'stock', label: 'Наличност' },
    { key: 'category_id', label: 'Категория' },
    { key: 'subcategory', label: 'Подкатегория' },
    { key: 'brand', label: 'Марка' },
    { key: 'weight', label: 'Грамаж' },
    { key: 'sku', label: 'SKU' },
    { key: 'barcode', label: 'Баркод' },
    { key: 'seo_title', label: 'SEO заглавие' },
    { key: 'seo_description', label: 'SEO описание' },
    { key: 'slug', label: 'Slug' },
    { key: 'tags', label: 'Тагове' },
    { key: 'origin_country', label: 'Произход' },
];

const thumb = (pid) => `${API}/products/${pid}/image?size=small`;
const fieldVal = (p, key) => {
    if (!p) return '';
    if (key === 'image') return '(снимка)';
    if (key === 'weight') return p.weight ? `${p.weight} ${p.weight_unit || ''}` : '';
    if (key === 'brand') return p.brand || '';
    if (key === 'category_id') return p.category_name || p.category_id || '';
    if (key === 'tags') return (p.tags || []).join(', ');
    const v = p[key];
    return v == null ? '' : String(v);
};

export default function DuplicatesManager({ open, onClose, token, onChanged }) {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [levelFilter, setLevelFilter] = useState('all');
    const [reasonFilter, setReasonFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [compare, setCompare] = useState(null); // {item, match}
    const [confirmDelete, setConfirmDelete] = useState(null); // {item, match, target: 'new'|'existing'}
    const [merge, setMerge] = useState(null); // {primary, secondary, choices}
    const [busy, setBusy] = useState(false);

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: statusFilter === 'all_open' ? 'all_open' : statusFilter };
            if (levelFilter !== 'all') params.level = levelFilter;
            const res = await axios.get(`${API}/admin/duplicates`, { ...auth, params });
            setItems(res.data.items || []);
            setCounts(res.data.counts || {});
        } catch (e) {
            toast.error('Грешка при зареждане на дубликатите');
        } finally {
            setLoading(false);
        }
    }, [levelFilter, statusFilter, token]); // eslint-disable-line

    useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

    const matchesReason = (it) => {
        if (reasonFilter === 'all') return true;
        const reasons = (it.matches || []).flatMap(m => m.reasons || []).join(' ');
        return reasons.includes(reasonFilter);
    };
    const visible = items.filter(matchesReason);

    const resolve = async (item, action, matchId = null, confirm = false, successMsg = null) => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/admin/duplicates/${item.product.product_id}/resolve`,
                { action, match_id: matchId, confirm }, auth);
            toast.success(successMsg || res.data.message);
            setCompare(null); setConfirmDelete(null);
            await fetchItems();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при действието');
        } finally {
            setBusy(false);
        }
    };

    const openMerge = (item, match) => {
        const primary = match.product; // existing product is primary by default
        const secondary = item.product;
        const choices = {};
        MERGE_FIELDS.forEach(f => {
            const pv = fieldVal(primary, f.key), sv = fieldVal(secondary, f.key);
            choices[f.key] = (!pv && sv) ? 'secondary' : 'primary';
        });
        setMerge({ primary, secondary, choices, item, match });
    };

    const swapMerge = () => setMerge(m => {
        const choices = {};
        Object.entries(m.choices).forEach(([k, v]) => { choices[k] = v === 'primary' ? 'secondary' : 'primary'; });
        return { ...m, primary: m.secondary, secondary: m.primary, choices };
    });

    const submitMerge = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/admin/duplicates/merge`, {
                primary_id: merge.primary.product_id,
                secondary_id: merge.secondary.product_id,
                fields: merge.choices,
            }, auth);
            toast.success(res.data.message);
            setMerge(null); setCompare(null);
            await fetchItems();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при обединяването');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="duplicates-manager-dialog">
                <DialogHeader>
                    <DialogTitle className="dark:text-white flex items-center gap-2">
                        <Copy className="h-5 w-5 text-orange-500" />
                        Възможни дубликати
                        <Button variant="ghost" size="icon" onClick={fetchItems} className="h-7 w-7" title="Обнови" data-testid="duplicates-refresh-btn">
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </DialogTitle>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Нищо не се изтрива автоматично — всяко действие изисква вашето изрично потвърждение.
                    </p>
                </DialogHeader>

                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={levelFilter} onValueChange={setLevelFilter}>
                        <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600" data-testid="duplicates-level-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Всички степени</SelectItem>
                            <SelectItem value="certain">Сигурни дубликати {counts.certain != null ? `(${counts.certain})` : ''}</SelectItem>
                            <SelectItem value="probable">Вероятни дубликати {counts.probable != null ? `(${counts.probable})` : ''}</SelectItem>
                            <SelectItem value="possible">Възможно сходни {counts.possible != null ? `(${counts.possible})` : ''}</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={reasonFilter} onValueChange={setReasonFilter}>
                        <SelectTrigger className="w-52 dark:bg-slate-700 dark:border-slate-600" data-testid="duplicates-reason-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Всички причини</SelectItem>
                            <SelectItem value="баркод">Еднакъв баркод</SelectItem>
                            <SelectItem value="SKU">Еднакво SKU</SelectItem>
                            <SelectItem value="снимка">Сходни изображения</SelectItem>
                            <SelectItem value="заглавие">Сходно заглавие</SelectItem>
                            <SelectItem value="грамаж">Еднакъв грамаж</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600" data-testid="duplicates-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="pending">Чакащи проверка</SelectItem>
                            <SelectItem value="postponed">Отложени</SelectItem>
                            <SelectItem value="all_open">Всички отворени</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="duplicates-list">
                    {loading && <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-orange-500" /></div>}
                    {!loading && visible.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="duplicates-empty">
                            🎉 Няма продукти, чакащи проверка за дубликати.
                        </div>
                    )}
                    {!loading && visible.map((it) => {
                        const top = it.matches[0];
                        const meta = LEVEL_META[top.level] || LEVEL_META.possible;
                        return (
                            <div key={it.product.product_id} className="border rounded-lg p-3 dark:border-slate-700 bg-white dark:bg-slate-800/60" data-testid={`duplicate-item-${it.product.product_id}`}>
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <Badge className={`border ${meta.cls}`}>{meta.label} · {Math.round(top.score * 100)}%</Badge>
                                    {it.status === 'postponed' && <Badge variant="outline" className="text-slate-500"><Clock className="h-3 w-3 mr-1" />Отложен</Badge>}
                                    <span className="text-xs text-slate-400 ml-auto">Защо: {top.reasons.join(', ')}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[{ p: it.product, tag: 'НОВ / ПРОВЕРЯВАН', ord: it.in_orders }, { p: top.product, tag: 'СЪЩЕСТВУВАЩ', ord: top.in_orders }].map(({ p, tag, ord }, i) => (
                                        <div key={i} className="flex gap-3 items-start p-2 rounded bg-slate-50 dark:bg-slate-900/40">
                                            <img src={thumb(p.product_id)} alt={p.name} className="w-16 h-16 object-cover rounded border dark:border-slate-600 shrink-0" />
                                            <div className="min-w-0 text-sm">
                                                <div className="text-[10px] font-bold text-slate-400">{tag}</div>
                                                <div className="font-medium dark:text-white truncate" title={p.name}>{p.name || '(без име)'}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                                    {p.brand || 'без марка'} · {p.weight ? `${p.weight} ${p.weight_unit || ''}` : 'без грамаж'} · {p.price != null ? `${p.price} €` : 'без цена'}
                                                </div>
                                                <div className="text-xs text-slate-400">
                                                    {p.is_draft ? '📝 Чернова' : '✅ Публикуван'}{ord ? ' · 📦 има поръчки' : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2 justify-end">
                                    <Button size="sm" variant="outline" onClick={() => setCompare({ item: it, match: top })} className="dark:border-slate-600" data-testid={`duplicate-compare-btn-${it.product.product_id}`}>
                                        <Scale className="h-4 w-4 mr-1" /> Сравни
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => resolve(it, 'not_duplicate')} disabled={busy}
                                        className="border-green-500 text-green-600 dark:text-green-400" data-testid={`duplicate-not-dup-btn-${it.product.product_id}`}>
                                        <CheckCheck className="h-4 w-4 mr-1" /> Не е дубликат
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => resolve(it, 'postpone')} disabled={busy} className="text-slate-500" data-testid={`duplicate-postpone-btn-${it.product.product_id}`}>
                                        <Clock className="h-4 w-4 mr-1" /> По-късно
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-end pt-2 border-t dark:border-slate-700">
                    <Button variant="outline" onClick={onClose} className="dark:border-slate-600" data-testid="duplicates-close-btn">Затвори</Button>
                </div>

                {/* ===== COMPARE DIALOG ===== */}
                {compare && (
                    <Dialog open onOpenChange={(o) => { if (!o) setCompare(null); }}>
                        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicate-compare-dialog">
                            <DialogHeader>
                                <DialogTitle className="dark:text-white">Сравнение на продукти</DialogTitle>
                                <div className="flex items-center gap-2 pt-1">
                                    <Badge className={`border ${(LEVEL_META[compare.match.level] || LEVEL_META.possible).cls}`}>
                                        {(LEVEL_META[compare.match.level] || LEVEL_META.possible).label} · {Math.round(compare.match.score * 100)}%
                                    </Badge>
                                    <span className="text-xs text-slate-500">{compare.match.reasons.join(', ')}</span>
                                </div>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                {[{ p: compare.item.product, tag: 'НОВ / ПРОВЕРЯВАН' }, { p: compare.match.product, tag: 'СЪЩЕСТВУВАЩ' }].map(({ p, tag }, i) => (
                                    <div key={i} className="text-center">
                                        <div className="text-[10px] font-bold text-slate-400 mb-1">{tag}</div>
                                        <img src={thumb(p.product_id)} alt={p.name} className="w-32 h-32 object-cover rounded border dark:border-slate-600 mx-auto" />
                                    </div>
                                ))}
                            </div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {COMPARE_FIELDS.map(f => {
                                        const a = compare.item.product, b = compare.match.product;
                                        const va = f.render ? f.render(a) : (a[f.key] ?? '—');
                                        const vb = f.render ? f.render(b) : (b[f.key] ?? '—');
                                        const same = String(va) === String(vb) && va !== '—' && va !== '' && va != null;
                                        return (
                                            <tr key={f.key} className="border-b dark:border-slate-700">
                                                <td className="py-1.5 pr-2 text-xs font-medium text-slate-500 w-28">{f.label}</td>
                                                <td className={`py-1.5 px-2 ${same ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'dark:text-white'}`}>{String(va || '—')}</td>
                                                <td className={`py-1.5 px-2 ${same ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'dark:text-white'}`}>{String(vb || '—')}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td className="py-1.5 pr-2 text-xs font-medium text-slate-500">Поръчки</td>
                                        <td className="py-1.5 px-2 dark:text-white">{compare.item.in_orders ? '📦 Има поръчки' : 'Няма'}</td>
                                        <td className="py-1.5 px-2 dark:text-white">{compare.match.in_orders ? '📦 Има поръчки' : 'Няма'}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="flex flex-wrap gap-2 justify-end pt-2">
                                <Button size="sm" variant="outline" onClick={() => resolve(compare.item, 'keep_both')} disabled={busy} className="border-green-500 text-green-600 dark:text-green-400" data-testid="compare-keep-both-btn">
                                    Запази и двата
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => resolve(compare.item, 'not_duplicate', compare.match.product_id)} disabled={busy} className="dark:border-slate-600 dark:text-white" data-testid="compare-not-duplicate-btn">
                                    Не е дубликат
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => resolve(compare.item, 'postpone')} disabled={busy} className="text-slate-500" data-testid="compare-postpone-btn">
                                    Пропусни и прегледай по-късно
                                </Button>
                                <Button size="sm" onClick={() => openMerge(compare.item, compare.match)} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="compare-merge-btn">
                                    <GitMerge className="h-4 w-4 mr-1" /> Обедини продуктите
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setConfirmDelete({ item: compare.item, match: compare.match, target: 'new' })} disabled={busy} className="border-red-400 text-red-600 dark:text-red-400" data-testid="compare-delete-new-btn">
                                    <Trash2 className="h-4 w-4 mr-1" /> Изтрий новия
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setConfirmDelete({ item: compare.item, match: compare.match, target: 'existing' })} disabled={busy} className="border-red-400 text-red-600 dark:text-red-400" data-testid="compare-delete-existing-btn">
                                    <Trash2 className="h-4 w-4 mr-1" /> Изтрий съществуващия
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}

                {/* ===== DELETE SECOND CONFIRMATION ===== */}
                {confirmDelete && (
                    <Dialog open onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
                        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-md" data-testid="duplicate-delete-confirm-dialog">
                            <DialogHeader>
                                <DialogTitle className="dark:text-white flex items-center gap-2 text-red-600">
                                    <ShieldAlert className="h-5 w-5" /> Потвърждение за изтриване
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <p className="text-sm dark:text-slate-300">
                                    Ще бъде изтрит: <strong>{confirmDelete.target === 'new' ? (confirmDelete.item.product.name || 'новият продукт') : (confirmDelete.match.product.name || 'съществуващият продукт')}</strong>
                                </p>
                                <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 p-3 text-sm text-red-800 dark:text-red-200">
                                    Сигурни ли сте, че искате да изтриете този продукт? Действието може да засегне наличности, поръчки или свързани категории.
                                    {((confirmDelete.target === 'new' && confirmDelete.item.in_orders) || (confirmDelete.target === 'existing' && confirmDelete.match.in_orders)) && (
                                        <p className="mt-2 font-medium">ℹ️ Продуктът участва в поръчки — ще бъде АРХИВИРАН вместо изтрит, за да не се наруши историята на поръчките.</p>
                                    )}
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={busy} className="dark:border-slate-600" data-testid="duplicate-delete-cancel-btn">Отказ</Button>
                                    <Button onClick={() => resolve(confirmDelete.item, confirmDelete.target === 'new' ? 'delete_new' : 'delete_existing', confirmDelete.match.product_id, true)}
                                        disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="duplicate-delete-confirm-btn">
                                        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />} Да, изтрий
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}

                {/* ===== MERGE DIALOG ===== */}
                {merge && (
                    <Dialog open onOpenChange={(o) => { if (!o) setMerge(null); }}>
                        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="duplicate-merge-dialog">
                            <DialogHeader>
                                <DialogTitle className="dark:text-white flex items-center gap-2">
                                    <GitMerge className="h-5 w-5 text-blue-500" /> Обединяване на продукти
                                </DialogTitle>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Изберете за всяко поле от кой продукт да остане стойността. Основният продукт остава активен, другият се архивира.
                                </p>
                            </DialogHeader>
                            <div className="flex items-center justify-between gap-2 text-sm">
                                <div className="flex-1 p-2 rounded bg-green-50 dark:bg-green-900/20 text-center">
                                    <div className="text-[10px] font-bold text-green-600">ОСНОВЕН (остава)</div>
                                    <div className="font-medium dark:text-white truncate">{merge.primary.name || merge.primary.product_id}</div>
                                </div>
                                <Button size="sm" variant="outline" onClick={swapMerge} className="dark:border-slate-600 shrink-0" data-testid="merge-swap-btn">⇄ Размени</Button>
                                <div className="flex-1 p-2 rounded bg-slate-100 dark:bg-slate-700/60 text-center">
                                    <div className="text-[10px] font-bold text-slate-500">ЩЕ БЪДЕ АРХИВИРАН</div>
                                    <div className="font-medium dark:text-white truncate">{merge.secondary.name || merge.secondary.product_id}</div>
                                </div>
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs text-slate-500 border-b dark:border-slate-700">
                                        <th className="text-left py-1">Поле</th>
                                        <th className="text-left py-1">Основен</th>
                                        <th className="text-left py-1">Архивиран</th>
                                        <th className="text-left py-1 w-40">Резултат (преглед)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {MERGE_FIELDS.map(f => {
                                        const pv = f.key === 'image' ? '' : fieldVal(merge.primary, f.key);
                                        const sv = f.key === 'image' ? '' : fieldVal(merge.secondary, f.key);
                                        if (f.key !== 'image' && !pv && !sv) return null;
                                        const chosen = merge.choices[f.key];
                                        const preview = f.key === 'image'
                                            ? (chosen === 'secondary' ? 'Снимка от архивирания' : 'Снимка от основния')
                                            : (chosen === 'secondary' ? sv : pv);
                                        return (
                                            <tr key={f.key} className="border-b dark:border-slate-700 align-top">
                                                <td className="py-1.5 pr-2 text-xs font-medium text-slate-500">{f.label}</td>
                                                {['primary', 'secondary'].map(side => (
                                                    <td key={side} className="py-1.5 pr-2">
                                                        <label className={`flex items-start gap-1.5 cursor-pointer rounded p-1 ${chosen === side ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}>
                                                            <input type="radio" name={`merge-${f.key}`} checked={chosen === side}
                                                                onChange={() => setMerge(m => ({ ...m, choices: { ...m.choices, [f.key]: side } }))}
                                                                className="mt-0.5 accent-blue-600" data-testid={`merge-${f.key}-${side}`} />
                                                            {f.key === 'image'
                                                                ? <img src={thumb(side === 'primary' ? merge.primary.product_id : merge.secondary.product_id)} alt="" className="w-10 h-10 object-cover rounded" />
                                                                : <span className="dark:text-white text-xs break-words max-w-[180px] line-clamp-3">{(side === 'primary' ? pv : sv) || '—'}</span>}
                                                        </label>
                                                    </td>
                                                ))}
                                                <td className="py-1.5 text-xs text-blue-700 dark:text-blue-300 break-words max-w-[160px]">
                                                    <span className="line-clamp-3">{preview || '—'}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="flex gap-2 justify-end pt-2">
                                <Button variant="outline" onClick={() => setMerge(null)} disabled={busy} className="dark:border-slate-600" data-testid="merge-cancel-btn">Отказ</Button>
                                <Button onClick={submitMerge} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="merge-confirm-btn">
                                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <GitMerge className="h-4 w-4 mr-1" />} Обедини
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </DialogContent>
        </Dialog>
    );
}
